import React from 'react';
import styled from 'styled-components';
import { IconButton, TextField as MuiTextField } from '@material-ui/core';
import { Select, NumberInput, TextField, DatePicker } from '../../../forms';
import { PencilIcon } from '../../../SVGs/Icons';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  padding: 12px 12px;
  border: 1px solid #dbdbdb;
  border-radius: 13px;
  position: relative;
  transition: opacity 200ms, transform 200ms;
  ${({ isDragging }) =>
    isDragging &&
    `
    opacity: 0.5;
    transform: scale(0.95);
  `}
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  min-width: 800px;

  ${({ isExpanded }) =>
    isExpanded && 'border-bottom: 1px solid #dbdbdb; padding-bottom: 5px;'}
`;

Header.Left = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 10px;
`;

Header.Right = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const EditButton = styled(IconButton).attrs({
  size: 'small',
  children: <PencilIcon />,
})`
  transition-property: all;

  &:not(:hover) {
    color: #999999;
  }
`;

EditButton.Text = styled.span`
  font-family: Poppins;
  font-weight: 400;
  font-size: 14px;
  color: #004bc4;
  cursor: pointer;
  margin-left: -5px;
`;

export const Body = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
`;

export const ContentBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const Section = styled.div`
  display: grid;
  gap: 10px 12px;
  grid-template-areas:
    'icon title'
    '-    content';
  grid-template-columns: auto 1fr;
  grid-template-rows: repeat(2, auto);
  align-items: center;
`;

Section.Icon = styled.span`
  grid-area: icon;
`;

Section.Title = styled.span`
  grid-area: title;
  color: #9b9b9b;

  font-family: Poppins;
  font-weight: 400;
  font-style: Regular;
  font-size: 13px;
`;

Section.Title.Highlight = styled.span`
  font-weight: 700;
  font-family: inherit;
`;

Section.Content = styled.div`
  grid-area: content;
`;

export const Thumbnail = styled.div`
  grid-area: thumbnail;
  width: 205px;
  height: 199px;
  border-radius: 13px;
  border: 1px solid #dbdbdb;
  background-color: #f2f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px;
  font-family: Poppins;
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  text-align: center;
  color: #616161;
`;

export const ExpandButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: all;

  svg {
    width: 100%;
    height: 100%;
  }

  &:hover {
    opacity: 0.7;
  }
`;

const ActionButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: all;
  padding: 4px;

  svg {
    width: 100%;
    height: 100%;
    transition: opacity 250ms !important;
  }

  &:hover {
    opacity: 0.7;
  }
`;

export const CopyButton = ActionButton;
export const DeleteButton = ActionButton;

export const ArrangeButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  pointer-events: all;
  padding: 4px;
  position: absolute;
  top: 10px;
  left: -30px;
  user-select: none;

  svg {
    width: 25px;
    height: 30px;
    transition: opacity 250ms !important;
  }

  &:hover {
    opacity: 0.7;
  }

  &:active {
    cursor: grabbing;
  }

  &[draggable='true'] {
    cursor: grab;
  }
`;

export const NameInput = styled(MuiTextField)`
  width: fit-content;
  max-width: 250px;

  > .MuiInputBase-root {
    color: #212121;
    font-family: Poppins;
    font-weight: 500;
    font-style: Medium;
    font-size: 14px;
    width: auto;

    &::before {
      border: none !important;
    }
  }

  .MuiInputBase-input {
    width: ${props =>
      props.value ? `${props.value.length + 1}ch` : '10ch'} !important;
    max-width: 250px;
  }
`;

export const ConditionPickerField = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 20px;
`;

export const ConditionPicker = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

ConditionPicker.DelayInput = styled(NumberInput)`
  width: 80px;
`;

ConditionPicker.UnitSelect = styled(Select)`
  margin-left: 10px;
  width: 130px;
`;

ConditionPicker.OrderSelect = styled(Select)`
  margin-left: 15px;
  width: 120px;
`;

ConditionPicker.EventSelect = styled(Select)`
  margin-left: 5px;
  width: 150px;
`;

ConditionPicker.OptionSeparator = styled.span`
  margin: 0 8px;
  color: #616161;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
`;

ConditionPicker.OptionSelect = styled(Select)`
  width: 120px;
`;

ConditionPicker.CustomDateInput = styled(DatePicker)`
  margin-left: 5px;
  width: 125px;
  padding-top: 10px;
  padding-bottom: 10px;
`;

export const TemplateEditor = styled.div`
  margin-top: 20px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const TemplateName = styled(TextField).attrs({ disabled: true })`
  width: ${({ width }) => (width ? `${width}px` : 'auto')};

  .MuiInputBase-root {
    color: #212121 !important;
    font-family: Poppins;
    font-weight: 500;
    font-style: Medium;
    font-size: 16px;
  }
`;

export const TemplateButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
